import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Film, Camera, Video, Award, Play, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App';

const DocumentairePage = () => {
  const [content, setContent] = useState({
    bio: '',
    subtitle: 'Réalisatrice · Scénariste · 2ème équipe action · Actrice',
    intro_text: '',
    photo_url: null,
    video_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/documentary`);
      setContent(response.data);
    } catch (err) {
      setError('Impossible de charger le contenu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Extraire l'ID YouTube d'une URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const documentaryServices = [
    {
      icon: Film,
      title: 'Réalisation documentaire',
      description: 'Conception et réalisation de documentaires sur mesure, du concept à la post-production.',
    },
    {
      icon: Camera,
      title: 'Direction de la photographie',
      description: 'Cadrage, lumière et esthétique visuelle pour un rendu cinématographique professionnel.',
    },
    {
      icon: Video,
      title: 'Captation & Tournage',
      description: 'Équipe technique complète pour vos tournages documentaires en France et à l\'international.',
    },
    {
      icon: Award,
      title: 'Post-production',
      description: 'Montage, étalonnage et mixage pour un résultat broadcast de qualité.',
    },
  ];

  const themes = [
    'Sport & Performance',
    'Métiers d\'art',
    'Coulisses du cinéma',
    'Portraits d\'artistes',
    'Making-of de productions',
    'Reportages d\'action',
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=1200')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Alexandra CHALLET
            </p>
            <h1 
              className="font-['Teko'] text-6xl md:text-8xl text-white mb-6"
              data-testid="documentary-title"
            >
              DOCUMENTAIRE
            </h1>
            <p className="font-['Barlow'] text-gray-300 text-lg md:text-xl leading-relaxed mb-8">
              {content.intro_text || `Réalisatrice de documentaires, Alexandra CHALLET met son expertise de l'action 
              et sa sensibilité artistique au service de projets documentaires uniques. 
              Du making-of de cascades aux portraits d'artistes, elle capture l'essence 
              de chaque histoire avec authenticité.`}
            </p>
            <Link to="/contact">
              <Button 
                className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-8 py-6 hover:bg-[#EAB308] transition-all duration-200"
                data-testid="documentary-cta"
              >
                Discuter de votre projet <ChevronRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-[#09090b]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Nos services
            </p>
            <h2 className="font-['Teko'] text-5xl md:text-6xl text-white">
              RÉALISATION DOCUMENTAIRE
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {documentaryServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-black border border-white/10 p-8 hover:border-[#FACC15]/30 transition-colors"
              >
                <service.icon className="w-12 h-12 text-[#FACC15] mb-6" />
                <h3 className="font-['Teko'] text-2xl text-white uppercase mb-3">
                  {service.title}
                </h3>
                <p className="font-['Barlow'] text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Themes */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
                Thématiques
              </p>
              <h2 className="font-['Teko'] text-5xl text-white mb-6">
                DOMAINES D'EXPERTISE
              </h2>
              <p className="font-['Barlow'] text-gray-400 leading-relaxed mb-8">
                Forte de son expérience dans le monde du cinéma d'action, Alexandra apporte 
                un regard unique sur les métiers de la performance et de la création artistique.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {themes.map((theme, index) => (
                  <motion.div
                    key={theme}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-2 h-2 bg-[#FACC15]" />
                    <span className="font-['Barlow'] text-white">{theme}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-video bg-[#09090b] border border-white/10 overflow-hidden">
                {content.video_url && getYouTubeEmbedUrl(content.video_url) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(content.video_url)}
                    title="Vidéo documentaire"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img 
                      src="https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800"
                      alt="Tournage documentaire"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="w-20 h-20 border-2 border-[#FACC15] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#FACC15]/20 transition-colors">
                        <Play size={32} className="text-[#FACC15] ml-1" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Alexandra Profile */}
      <section className="py-24 bg-[#09090b] border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="aspect-square bg-[#FACC15]/10 flex items-center justify-center overflow-hidden">
                {content.photo_url ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}${content.photo_url}`}
                    alt="Alexandra Challet"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-['Teko'] text-8xl text-[#FACC15]">AC</span>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h2 className="font-['Teko'] text-4xl md:text-5xl text-white mb-4">
                ALEXANDRA CHALLET
              </h2>
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-wider text-sm mb-6">
                {content.subtitle || 'Réalisatrice · Scénariste · 2ème équipe action · Actrice'}
              </p>
              {content.bio ? (
                <div className="font-['Barlow'] text-gray-300 leading-relaxed mb-8 whitespace-pre-line">
                  {content.bio}
                </div>
              ) : (
                <>
                  <p className="font-['Barlow'] text-gray-300 leading-relaxed mb-6">
                    Avec plus de 15 ans d'expérience dans l'industrie du cinéma, Alexandra combine 
                    sa passion pour l'action et le documentaire. Son approche unique permet de capturer 
                    des moments authentiques tout en apportant une dimension cinématographique 
                    à chaque projet.
                  </p>
                  <p className="font-['Barlow'] text-gray-300 leading-relaxed mb-8">
                    Spécialisée dans les documentaires sur les métiers de la cascade, du sport extrême 
                    et des coulisses de productions, elle sait raconter des histoires qui touchent 
                    et inspirent.
                  </p>
                </>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0629653524">
                  <Button 
                    className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]"
                  >
                    06 29 65 35 24
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10"
                  >
                    Envoyer un message
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-['Teko'] text-5xl text-white mb-6">
              UN PROJET DOCUMENTAIRE ?
            </h2>
            <p className="font-['Barlow'] text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Vous avez une histoire à raconter ? Un sujet qui vous passionne ? 
              Discutons ensemble de votre projet documentaire.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-10 py-6 text-lg hover:bg-[#EAB308]"
              >
                Contactez-nous
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DocumentairePage;
